<script setup lang="ts">
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { enterFullscreen, exitFullscreen, isFullscreen, isFullscreenAvailable } from '@@/bits/fullscreen'
import { __ } from '@@/bits/intl'
import { SLIDESHOW_ANALYTICS_ID } from '@@/bits/slideshow'
import type { OzMenuRowsRow } from '@@/library/v4/components/OzMenuRows.vue'
import OzMenuRows, { OzMenuRowsFocusStyle, OzMenuRowsSizePreset } from '@@/library/v4/components/OzMenuRows.vue'
import OzPopover from '@@/library/v4/components/OzPopover.vue'
import { exitSlideshow } from '@@/native_bridge/actions'
import postMessage from '@@/native_bridge/post_message'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useSlideshowMenus } from '@@/vuecomposables/slideshow_menus'
import { useSlideshowPages } from '@@/vuecomposables/slideshow_pages'
import { useSlideshowPanels } from '@@/vuecomposables/slideshow_panels'
import { useSlideshowQRCode } from '@@/vuecomposables/slideshow_qr_code'
import { useSlideshowStyles } from '@@/vuecomposables/slideshow_styles'
import type { ComputePositionConfig } from '@floating-ui/vue'
import { offset, shift } from '@floating-ui/vue'
import { compact } from 'es-toolkit'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

defineProps<{
  anchorElement: Element
}>()

const { isEmbedded } = storeToRefs(useSurfaceStore())
const { onRestart, isFirstPage } = useSlideshowPages()
const { isMoreMenuOpen } = useSlideshowMenus()
const { isSharePanelOpen, isLanguagePanelOpen, isAccessibilitySettingsModalOpen } = useSlideshowPanels()
const { toggleQRCode, isQrCodeShown } = useSlideshowQRCode()

const { hasDarkBackgroundLuminance, hasLightBackgroundLuminance } = useSlideshowStyles()

const surfaceTranslationsStore = useSurfaceTranslationsStore()

enum ActionType {
  ShowQRCode = 'ShowQRCode',
  HideQRCode = 'HideQRCode',
  Restart = 'Restart',
  EnterFullscreen = 'EnterFullscreen',
  ExitFullscreen = 'ExitFullscreen',
  ExitSlideshow = 'ExitSlideshow',
  Share = 'Share',
  Language = 'Language',
  AccessibilitySettings = 'AccessibilitySettings',
}

const popoverConfig: Partial<ComputePositionConfig> = {
  placement: 'top-end',
  middleware: [
    offset({
      mainAxis: 4,
      crossAxis: 16,
    }),
    shift({
      padding: 16,
    }),
  ],
}

const actionListGroups = computed((): OzMenuRowsRow<ActionType>[][] => {
  const shareRow = {
    icon: 'share_arrow_outline',
    text: __('Share'),
    eventPayload: ActionType.Share,
    analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-share-button'],
    testId: 'slideshowMoreMenuShareButton',
  }
  const showQRCodeRow = {
    icon: 'qr',
    text: __('Show QR code'),
    eventPayload: ActionType.ShowQRCode,
    analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-show-qr-code-button'],
  }
  const hideQRCodeRow = {
    icon: 'qr',
    text: __('Hide QR code'),
    eventPayload: ActionType.HideQRCode,
    analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-hide-qr-code-button'],
  }
  const accessibilitySettingsRow = {
    icon: 'accessibility_outline',
    text: __('Accessibility settings'),
    eventPayload: ActionType.AccessibilitySettings,
    analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-accessibility-settings-button'],
    testId: 'slideshowMoreMenuAccessibilityButton',
  }
  const restartRow = {
    icon: 'undo',
    text: __('Restart'),
    eventPayload: ActionType.Restart,
    analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-restart-button'],
    testId: 'slideshowMoreMenuRestartButton',
  }
  const exitSlideshowRow = {
    icon: 'exit_thin',
    text: __('Exit slideshow'),
    eventPayload: ActionType.ExitSlideshow,
    analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-exit-button'],
  }
  const enterFullscreenRow = {
    icon: 'fullscreen',
    text: __('Fullscreen'),
    eventPayload: ActionType.EnterFullscreen,
    analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-enter-fullscreen-button'],
    testId: 'slideshowMoreMenuFullscreenButton',
  }
  const exitFullscreenRow = {
    icon: 'fullscreen_exit',
    text: __('Exit fullscreen'),
    eventPayload: ActionType.ExitFullscreen,
    analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-exit-fullscreen-button'],
  }
  const languageRow = {
    icon: 'globe',
    text: __('Language'),
    eventPayload: ActionType.Language,
    analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-language-button'],
    rightLabel: surfaceTranslationsStore.currentLanguageDisplayName,
    textStyleClass: '!min-w-fit text-body-small',
    rightLabelClasses: 'ms-2 truncate',
  }

  const restartRowIfNotFirstPage = isFirstPage.value ? undefined : restartRow
  const qrCodeRowIfFirstPage = isFirstPage.value ? (isQrCodeShown.value ? hideQRCodeRow : showQRCodeRow) : undefined
  const fullscreenRow = isFullscreenAvailable ? (isFullscreen() ? exitFullscreenRow : enterFullscreenRow) : undefined

  let group: Array<OzMenuRowsRow<ActionType> | undefined>
  if (isEmbedded.value) {
    group = [qrCodeRowIfFirstPage, restartRowIfNotFirstPage, fullscreenRow]
  } else if (device.app) {
    group = [qrCodeRowIfFirstPage, shareRow, restartRowIfNotFirstPage, fullscreenRow, exitSlideshowRow]
  } else {
    // For web, more menu is only shown on mobile viewports
    group = isAppUsing('translatePadlet')
      ? [accessibilitySettingsRow, shareRow, languageRow, restartRowIfNotFirstPage, fullscreenRow]
      : [accessibilitySettingsRow, shareRow, restartRowIfNotFirstPage, fullscreenRow]
  }
  return [compact(group)] // remove undefined rows
})

const closeMenu = () => {
  isMoreMenuOpen.value = false
}

const handleSwipeDown = ({ isAtTopBeforeDragging }: { isAtTopBeforeDragging: boolean }): void => {
  if (isAtTopBeforeDragging) {
    return
  }
  closeMenu()
}

const showShareOverlay = () => {
  isSharePanelOpen.value = true
}

const showLanguagePanel = () => {
  isLanguagePanelOpen.value = true
}

const showAccessibilitySettingsModal = () => {
  isAccessibilitySettingsModalOpen.value = true
}

const handleAction = async (_event: unknown, actionType: ActionType) => {
  switch (actionType) {
    case ActionType.Restart:
      onRestart()
      break
    case ActionType.EnterFullscreen:
      enterFullscreen()
      break
    case ActionType.ExitFullscreen:
      exitFullscreen()
      break
    case ActionType.ExitSlideshow:
      postMessage(exitSlideshow())
      break
    case ActionType.Share:
      showShareOverlay()
      break
    case ActionType.ShowQRCode:
    case ActionType.HideQRCode:
      toggleQRCode()
      break
    case ActionType.Language:
      showLanguagePanel()
      break
    case ActionType.AccessibilitySettings:
      showAccessibilitySettingsModal()
      break
    default:
      break
  }

  closeMenu()
}
</script>

<template>
  <OzPopover
    role="dialog"
    :aria-label="__('Slideshow more menu')"
    :dark-mode="hasDarkBackgroundLuminance"
    :anchor="anchorElement"
    :config="popoverConfig"
    :should-fade-in="true"
    :radius="16"
    class="select-none shadow-elevation-3"
    data-testid="slideshowMoreMenu"
    @swipedown="handleSwipeDown"
    @scrim-click="closeMenu"
    @scrim-esc="closeMenu"
  >
    <div
      :class="[
        'w-64',
        'p-0 gap-2 rounded-2xl overflow-hidden grid grid-flow-row grid-cols-1',
        {
          'bg-grey-50': hasLightBackgroundLuminance,
          'bg-popover-overlay-dark': hasDarkBackgroundLuminance,
        },
      ]"
      role="menu"
    >
      <div
        v-for="(actionList, i) in actionListGroups"
        :key="i"
        :class="[
          'rounded-none',
          {
            'bg-common-ui-l86': hasLightBackgroundLuminance,
            'bg-common-ui-d86': hasDarkBackgroundLuminance,
          },
        ]"
      >
        <OzMenuRows
          :size-preset="OzMenuRowsSizePreset.H44px"
          :focus-style="OzMenuRowsFocusStyle.Bubble200"
          :dark-mode="hasDarkBackgroundLuminance"
          :rows="actionList"
          :accessibility-role="'group'"
          @select-row="handleAction"
        />
      </div>
    </div>
  </OzPopover>
</template>
